/***
 *
 *   BRAND SCOPE
 *   Applies a tenant's derived palette to its subtree by setting the primary
 *   and accent CSS variables as an inline style. Used by the two white-labelled
 *   public project routes.
 *
 *   Inline style rather than document.documentElement.style.setProperty, for
 *   three reasons:
 *
 *   - Blast radius. Only the branded routes are affected, so nothing can leak
 *     into the dashboard or survive a client-side navigation away from here.
 *   - Dark mode falls out for free. An inline declaration outranks every
 *     selector, `.dark` included, so one code path covers both themes. Writing
 *     the same vars onto <html> would ALSO beat `.dark` — silently killing the
 *     dark-mode accent tuning in css/tokens.css for the whole app.
 *   - No teardown to forget. There is nothing to clean up on unmount.
 *
 *   Precedent: components/auth/brandingBackdrop.js already sets a custom
 *   property (--ticket-tilt) per element this way.
 *
 *   KNOWN LIMITATION
 *   components/view/view.js renders Notification, Loader, Modal and Pannel as
 *   SIBLINGS of the layout, not children, so they sit outside this scope and
 *   keep the stock palette. Nothing brand-tokened renders there on the two
 *   public routes — notifications colour from --error / --success — but this
 *   needs a decision before the dashboard is branded, where modals are
 *   everywhere.
 *
 *   PROPS
 *   color: the tenant's hex, or null/undefined for the Valetify default
 *   className: injected classes on the wrapper (use 'contents' where the
 *              parent's layout depends on the child being a direct descendant)
 *
 **********/

import { useMemo } from 'react';
import { useTheme } from 'components/lib';
import { brandCssVars } from 'utilities/brand';

export function BrandScope(props) {
    const { isDark } = useTheme();

    const vars = useMemo(
        () => brandCssVars(props.color, { isDark: isDark }),
        [props.color, isDark],
    );

    // No brand, no wrapper. An unbranded page renders exactly the DOM it did
    // before this component existed, which is what keeps the default path
    // impossible to regress.
    if (!vars) return props.children;

    return (
        <div style={vars} className={props.className}>
            {props.children}
        </div>
    );
}
